import { Button, Spinner } from '@consigli/facade';
import { useGetProjectParticipantsQuery, useProjectId, useServiceName } from '@consigli/hooks';
import { type ProjectActionItem, type ProjectParticipant } from '@consigli/types';
import { useCallback, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { FiArrowLeft } from 'react-icons/fi';
import { useNavigate } from 'react-router';

import { Route } from '@/routes';

import { ActionItemsFilters } from './components/action-items-filters';
import { ActionItemsGroupSelector } from './components/action-items-group-selector';
import {
  ActionItemsTable,
  type ActionItemColumn,
  type GroupByOption,
} from './components/action-items-table';
import { DeleteConfirmModal } from './components/delete-confirm-modal';
import { TranscriptSourceModal } from './components/transcript-source-modal';
import { useActionItemsFilters } from './hooks/use-action-items-filters';
import { useProjectActionItems } from './hooks/use-project-action-items';
import { useTopics } from './hooks/use-topics';
import { useTranscriptSource } from './hooks/use-transcript-source';

const EMPTY_PARTICIPANTS: ProjectParticipant[] = [];

// All columns available for project-level view
const PROJECT_COLUMNS: ActionItemColumn[] = [
  'title',
  'person',
  'deadline',
  'topic',
  'status',
  'meeting',
];

export const ActionItemsOverviewPage: FC = () => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const serviceName = useServiceName();
  const navigate = useNavigate();

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [groupBy, setGroupBy] = useState<GroupByOption>('person');

  // Use the data fetching and delete hook
  const {
    data,
    isLoading,
    isError,
    error,
    deleteModalItem,
    setDeleteModalItem,
    handleDeleteConfirm,
    handleBulkReassignItems,
    handleBulkUpdateStatus,
    handleBulkUpdateDeadline,
    handleBulkAddTopic,
  } = useProjectActionItems();

  // Transcript source panel — the transcript is fetched when an item is opened.
  const {
    sourceItem,
    segmentIds: sourceSegmentIds,
    entries: sourceEntries,
    meeting: sourceMeeting,
    isLoading: isSourceLoading,
    hasError: hasSourceError,
    showSource,
    closeSource,
    retry: retrySource,
  } = useTranscriptSource();

  // Project participants power the reassign picker.
  const { data: participants = EMPTY_PARTICIPANTS } = useGetProjectParticipantsQuery(
    { projectId },
    { skip: !projectId },
  );

  // Use the filter hook
  const {
    searchText,
    statusFilters,
    personFilters,
    topicFilters,
    setStatusFilters,
    setPersonFilters,
    setTopicFilters,
    handleSearchChange,
    clearFilters,
    filterOptions,
    filteredItems,
    hasActiveFilters,
  } = useActionItemsFilters(data);

  // Use the topics hook with topics from filter options
  const topicsFromData = filterOptions.topics.map((t) => t.value);
  const { allTopics, handleAddCustomTopic } = useTopics({
    initialTopics: topicsFromData,
  });

  const handleBackToOverview = useCallback(() => {
    navigate(`/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.DASHBOARD}`);
  }, [navigate, projectId, serviceName]);

  const toggleGroup = useCallback((groupKey: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  }, []);

  const handleGroupByChange = useCallback((newGroupBy: GroupByOption) => {
    setGroupBy(newGroupBy);
    setExpandedGroups(new Set());
  }, []);

  const totalItems = data?.length ?? 0;
  const filteredCount = filteredItems.length;

  if (isLoading) {
    return (
      <div className="flex-1 overflow-auto bg-background p-12">
        <div className="flex items-center justify-center py-12">
          <Spinner size="xsmall" />
          <span className="ml-2 text-gray-600">{t('meeting-secretary.action-items.loading')}</span>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex-1 overflow-auto bg-background p-12">
        <div className="text-center py-12">
          <p className="text-red-600">{t('meeting-secretary.action-items.error-loading')}</p>
          <p className="text-sm text-gray-500 mt-2">
            {error instanceof Error
              ? error.message
              : ((error as { data?: { detail?: string } })?.data?.detail ?? 'Unknown error')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto bg-background p-12">
      {/* Back Button */}
      <Button tertiary icon={FiArrowLeft} onClick={handleBackToOverview} className="mb-4 !px-0">
        {t('meeting-secretary.action-items.back-to-overview')}
      </Button>

      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('meeting-secretary.action-items.title')}</h1>
          <p className="text-gray-600 mt-2">
            {filteredCount === totalItems
              ? t('meeting-secretary.action-items.subtitle', { count: totalItems })
              : t('meeting-secretary.action-items.subtitle-filtered', {
                  filtered: filteredCount,
                  total: totalItems,
                })}
          </p>
        </div>
        {hasActiveFilters && (
          <Button tertiary onClick={clearFilters}>
            × {t('meeting-secretary.action-items.clear-filters')}
          </Button>
        )}
      </div>

      {/* Group By Selector */}
      <ActionItemsGroupSelector groupBy={groupBy} onGroupByChange={handleGroupByChange} />

      {/* Search and Filters */}
      <ActionItemsFilters
        searchText={searchText}
        statusFilters={statusFilters}
        personFilters={personFilters}
        topicFilters={topicFilters}
        filterOptions={filterOptions}
        onSearchChange={handleSearchChange}
        onStatusChange={setStatusFilters}
        onPersonChange={setPersonFilters}
        onTopicChange={setTopicFilters}
      />

      {/* Content */}
      {totalItems === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <p className="text-gray-500">{t('meeting-secretary.action-items.no-items')}</p>
        </div>
      ) : filteredCount === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <p className="text-gray-500">{t('meeting-secretary.action-items.no-matches')}</p>
        </div>
      ) : (
        <ActionItemsTable
          items={filteredItems}
          columns={PROJECT_COLUMNS}
          groupBy={groupBy}
          participants={participants}
          topics={allTopics}
          expandedGroups={expandedGroups}
          onToggleGroup={toggleGroup}
          onDelete={(item) => setDeleteModalItem(item as ProjectActionItem)}
          onAddCustomTopic={handleAddCustomTopic}
          onBulkReassignItems={handleBulkReassignItems}
          onBulkUpdateStatus={handleBulkUpdateStatus}
          onBulkUpdateDeadline={handleBulkUpdateDeadline}
          onBulkAddTopic={handleBulkAddTopic}
          onShowSource={showSource}
        />
      )}

      <TranscriptSourceModal
        isOpen={!!sourceItem}
        onClose={closeSource}
        meeting={sourceMeeting}
        entries={sourceEntries}
        segmentIds={sourceSegmentIds}
        isLoading={isSourceLoading}
        hasError={hasSourceError}
        onRetry={retrySource}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={!!deleteModalItem}
        onClose={() => setDeleteModalItem(null)}
        onConfirm={handleDeleteConfirm}
        titleKey="meeting-secretary.meetings.delete-action-item"
        messageKey="meeting-secretary.meetings.confirm-delete-action-item"
      />
    </div>
  );
};
